import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  GripVertical,
  Phone,
  MessageCircle,
  Globe,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Loader2,
  AlertCircle,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ApiService from '../services/api';

// Contact type options
const CONTACT_TYPES = [
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'website', label: 'Website', icon: Globe },
  { value: 'social', label: 'Social Media', icon: Facebook },
  { value: 'other', label: 'Other', icon: Globe },
];

// Icon mapping for contact types
const getContactIcon = (type, iconName) => {
  const iconMap = {
    phone: Phone,
    whatsapp: MessageCircle,
    email: Mail,
    website: Globe,
    facebook: Facebook,
    twitter: Twitter,
    instagram: Instagram,
    linkedin: Linkedin,
  };
  
  const IconComponent = iconMap[iconName?.toLowerCase()] || iconMap[type] || Globe;
  return <IconComponent size={16} className="text-gray-600" />;
};

const Contact = () => {
  const [contactOptions, setContactOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [deletingContactId, setDeletingContactId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    icon: '',
    type: 'phone',
    value: '',
    isActive: true,
    order: 0,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch contact options
  const fetchContactOptions = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllContactOptions();
      setContactOptions(response.data || []);
    } catch (error) {
      console.error('Error fetching contact options:', error);
      toast.error('Failed to fetch contact options');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactOptions();
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.icon.trim()) {
      newErrors.icon = 'Icon is required';
    }
    if (!formData.value.trim()) {
      newErrors.value = 'Value is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create/edit contact
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      if (editingContact) {
        const response = await ApiService.updateContactOption(editingContact.id, formData);
        toast.success('Contact option updated successfully');
      } else {
        const response = await ApiService.createContactOption(formData);
        toast.success('Contact option created successfully');
      }

      handleCloseDialog();
      fetchContactOptions();
    } catch (error) {
      console.error('Error saving contact option:', error);
      toast.error(error.message || 'Failed to save contact option');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete contact
  const handleDelete = async () => {
    try {
      await ApiService.deleteContactOption(deletingContactId);
      toast.success('Contact option deleted successfully');
      setOpenDeleteDialog(false);
      setDeletingContactId(null);
      fetchContactOptions();
    } catch (error) {
      console.error('Error deleting contact option:', error);
      toast.error('Failed to delete contact option');
    }
  };

  // Handle toggle active status
  const handleToggleActive = async (contactId) => {
    try {
      await ApiService.toggleContactOption(contactId);
      toast.success('Contact option status updated');
      fetchContactOptions();
    } catch (error) {
      console.error('Error toggling contact status:', error);
      toast.error('Failed to update contact status');
    }
  };

  // Open dialog for create/edit
  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        title: contact.title,
        subtitle: contact.subtitle || '',
        icon: contact.icon,
        type: contact.type,
        value: contact.value,
        isActive: contact.isActive,
        order: contact.order,
      });
    } else {
      setEditingContact(null);
      setFormData({
        title: '',
        subtitle: '',
        icon: '',
        type: 'phone',
        value: '',
        isActive: true,
        order: contactOptions.length,
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingContact(null);
    setFormData({
      title: '',
      subtitle: '',
      icon: '',
      type: 'phone',
      value: '',
      isActive: true,
      order: 0,
    });
    setErrors({});
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (contactId) => {
    setDeletingContactId(contactId);
    setOpenDeleteDialog(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Contact Options Management</h1>
              <p className="text-gray-600 mt-1">Manage contact options displayed in the mobile app</p>
            </div>
            <button
              onClick={() => handleOpenDialog()}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Add Contact Option
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {contactOptions.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No contact options found</h3>
              <p className="text-gray-600">Click "Add Contact Option" to create your first one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Icon</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contactOptions.map((contact) => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <GripVertical className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-900">{contact.order}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getContactIcon(contact.type, contact.icon)}
                          <span className="ml-2 text-xs text-gray-500">{contact.icon}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{contact.title}</div>
                          {contact.subtitle && (
                            <div className="text-xs text-gray-500">{contact.subtitle}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          {contact.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 max-w-48 truncate" title={contact.value}>
                          {contact.value}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          contact.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {contact.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleToggleActive(contact.id)}
                            className={`p-1 rounded-md hover:bg-gray-100 ${
                              contact.isActive ? 'text-green-600' : 'text-gray-400'
                            }`}
                            title={contact.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {contact.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                          </button>
                          <button
                            onClick={() => handleOpenDialog(contact)}
                            className="p-1 rounded-md hover:bg-gray-100 text-blue-600"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteDialog(contact.id)}
                            className="p-1 rounded-md hover:bg-gray-100 text-red-600"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingContact ? 'Edit Contact Option' : 'Create Contact Option'}
              </h2>
              <button
                onClick={handleCloseDialog}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => handleInputChange('subtitle', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Optional description or additional info"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {CONTACT_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon Name *
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => handleInputChange('icon', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      errors.icon ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="e.g., phone, whatsapp, email"
                  />
                  {errors.icon && (
                    <p className="text-red-500 text-sm mt-1">{errors.icon}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Value *
                  </label>
                  {formData.type === 'website' || formData.type === 'social' ? (
                    <textarea
                      value={formData.value}
                      onChange={(e) => handleInputChange('value', e.target.value)}
                      rows={2}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        errors.value ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Phone number, email, URL, etc."
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.value}
                      onChange={(e) => handleInputChange('value', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                        errors.value ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Phone number, email, URL, etc."
                    />
                  )}
                  {errors.value && (
                    <p className="text-red-500 text-sm mt-1">{errors.value}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="0"
                  />
                  <p className="text-gray-500 text-sm mt-1">Lower numbers appear first</p>
                </div>
                
                <div className="flex items-center">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={handleCloseDialog}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {editingContact ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Confirm Delete</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this contact option? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setOpenDeleteDialog(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
